import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiMessageSquare, FiX } from "react-icons/fi";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCohort } from "@/hooks/cohort/useUserCohort";
import { useChat } from "@/hooks/useChat";
import ChatWindow from "@/components/chat/ChatWindow";

/**
 * Participant-side entry point for the trainer chat: a bell in the header that
 * carries an unread badge, plus an unmissable banner the moment a message
 * arrives. Rendered for participants only.
 */
const ParticipantChatLauncher: React.FC = () => {
  const { currentUser } = useAuth();
  const { cohort } = useUserCohort();
  const [isOpen, setIsOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const { unreadCount, thread } = useChat({
    cohortId: cohort?.id,
    cohortName: cohort?.name,
    participantEmail: currentUser?.email,
    participantName: currentUser?.name,
    isOpen,
  });

  // A fresh message brings the banner back even if it was dismissed earlier.
  useEffect(() => {
    if (unreadCount > 0) setBannerDismissed(false);
  }, [unreadCount]);

  if (!cohort?.id || !currentUser?.email) return null;

  const hasUnread = unreadCount > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={
          hasUnread
            ? `Messages — ${unreadCount} unread`
            : "Messages from your trainer"
        }
        title="Messages"
        className={`relative inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
          hasUnread
            ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        }`}
      >
        <span className="relative flex">
          <FiMessageSquare size={18} />
          {hasUnread && (
            <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </span>
        <span className="hidden sm:inline">
          {hasUnread ? "New message" : "Messages"}
        </span>
        {hasUnread && (
          <span className="absolute inset-0 -z-10 animate-ping rounded-lg bg-red-200" />
        )}
      </button>

      {/* Impossible-to-miss prompt while something is unread. Rendered into
          document.body via a portal: the header this launcher lives in has a
          CSS transform, which would otherwise hijack position:fixed and pin
          the banner to the header instead of the viewport. */}
      {hasUnread &&
        !bannerDismissed &&
        !isOpen &&
        createPortal(
          <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-4">
          <div className="flex w-full max-w-md items-center gap-3 rounded-xl border border-red-200 bg-white p-4 shadow-lg">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <FiMessageSquare size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">
                You have {unreadCount} new message
                {unreadCount === 1 ? "" : "s"} from your trainer
              </p>
              {thread?.lastMessage && (
                <p className="truncate text-sm text-gray-500">
                  {thread.lastMessage}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Read
            </button>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setBannerDismissed(true)}
              className="shrink-0 text-gray-400 hover:text-gray-600"
            >
              <FiX />
            </button>
          </div>
          </div>,
          document.body
        )}

      <ChatWindow
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        cohortId={cohort.id}
        cohortName={cohort.name}
        participantEmail={currentUser.email}
        participantName={currentUser.name}
        title="Messages with your trainer"
        subtitle={cohort.name}
      />
    </>
  );
};

export default ParticipantChatLauncher;
